/**
 * One guard per surface (sen-5 F6). The lazy route `import()` and the upload
 * worker's `importScripts` each get exactly one recovery reload, released only
 * by THAT surface proving it recovered. A single shared key let a route import
 * succeeding — which happens on every boot, usually before the worker has
 * fetched its second script — clear the marker the worker had just set, so a
 * worker chunk failure that survived a fresh `index.html` reloaded the page on
 * every boot.
 */
const ROUTE_RELOAD_GUARD_KEY = 'mfw:stale-chunk-reload';
const WORKER_RELOAD_GUARD_KEY = 'mfw:stale-chunk-reload:worker';
const CHAT_RELOAD_GUARD_KEY = 'mfw:stale-chunk-reload:chat';

const CHUNK_ERROR_MESSAGE =
  /loading (css )?chunk|failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to execute 'importScripts'/i;

export function isChunkLoadError(error: unknown): error is Error {
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'ChunkLoadError' || CHUNK_ERROR_MESSAGE.test(error.message)
  );
}

export type StaleChunkRecoveryDeps = {
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  reload: () => void;
};

/**
 * After a deploy the previous build's hashed chunks are gone, so a lazy route
 * import 404s and would crash the app. A single reload fetches the new
 * index.html (served no-cache) and recovers transparently; the sessionStorage
 * guard prevents a reload loop when the import keeps failing for another
 * reason.
 */
export function createStaleChunkRecovery({
  storage,
  reload,
}: StaleChunkRecoveryDeps) {
  /** The one-shot reload behind a guard key; returns true when it was triggered. */
  const reloadOnce = (guardKey: string) => {
    try {
      if (storage.getItem(guardKey)) return false;
      storage.setItem(guardKey, String(Date.now()));
      reload();
      return true;
    } catch {
      return false;
    }
  };
  /** Returns true when a recovery reload was triggered. */
  const attempt = (guardKey: string, error: unknown) =>
    isChunkLoadError(error) ? reloadOnce(guardKey) : false;
  const release = (guardKey: string) => {
    try {
      storage.removeItem(guardKey);
    } catch {
      // storage unavailable — nothing to clear
    }
  };

  // Whether the CURRENT upload worker has posted `OnReady`. This recovery is a
  // page-global singleton but the Worker is not — `AppProviders` is mounted per
  // route branch, so the login flow alone constructs a second Worker on the same
  // page (and StrictMode re-runs the effect in development). `onWorkerStarted`
  // resets this for each new Worker: readiness belongs to the Worker that
  // proved it.
  let workerReady = false;

  return {
    /**
     * V2-30: the upload worker loads its vendor chunk with `importScripts`,
     * and a stale worker asking for a hash the deploy removed dies inside the
     * worker — the route-level `import()` guard never sees it. A Worker's
     * `onerror` hands over an ErrorEvent: `message` carries the text, `error`
     * is usually null. Same one-shot reload, its own sessionStorage guard.
     *
     * sen-8 U5: the worker's OWN script 404ing after a deploy is the
     * neighbouring case, and it looks different. A script that never fetched
     * never ran, so nothing threw — the HTML Standard fires a plain `error`
     * `Event` at the Worker object, with no `message` for the regex (lib.dom
     * types `onerror` as `ErrorEvent`, which is why the compiler never said
     * so). Before the worker has proved it came up, a message-less error can
     * only be that load failure; after `OnReady` it is a runtime error and is
     * left alone.
     */
    onWorkerError(event: Event | { error?: unknown; message?: string }) {
      // A plain `Event` has neither field; an `ErrorEvent` has both.
      const { error, message } = event as { error?: unknown; message?: string };
      if (error instanceof Error) {
        return attempt(WORKER_RELOAD_GUARD_KEY, error);
      }
      if (!message) {
        return workerReady ? false : reloadOnce(WORKER_RELOAD_GUARD_KEY);
      }
      return attempt(WORKER_RELOAD_GUARD_KEY, new Error(message));
    },
    /**
     * The worker's own proof of recovery: its module code ran, which it can
     * only do after every `importScripts` succeeded. A route import succeeding
     * proves nothing about the worker's chunk graph and must not release this.
     */
    onWorkerReady() {
      workerReady = true;
      release(WORKER_RELOAD_GUARD_KEY);
    },
    /**
     * A new Worker was just constructed and has not said anything yet. Called
     * synchronously at construction, before its listeners are attached, so no
     * message from it can predate the reset.
     */
    onWorkerStarted() {
      workerReady = false;
    },
    /** Returns true when a recovery reload was triggered. */
    onImportError(error: unknown) {
      return attempt(ROUTE_RELOAD_GUARD_KEY, error);
    },
    onImportSuccess() {
      release(ROUTE_RELOAD_GUARD_KEY);
    },
    /**
     * The header's lazy chat chunk (review 2026-10-01 follow-up #4) loads on
     * every authenticated screen, independently of any route, so it is a
     * surface of its own — with its own guard, released only by the chat
     * chunk itself loading (the sen-5 F6 rule).
     */
    onChatImportError(error: unknown) {
      return attempt(CHAT_RELOAD_GUARD_KEY, error);
    },
    onChatImportSuccess() {
      release(CHAT_RELOAD_GUARD_KEY);
    },
  };
}
